import { ComponentPublicInstance } from "@vue/runtime-core";
import { formatApiDate } from "@/utils/dates/dateFormats";
import { ProjectSchedulesUserDto } from "@/core/features/project-schedule-users/project-schedules-user.dto";
import { addDays, eachDayOfInterval } from "date-fns";
import { TimeOffDto } from "@/core/features/times-off/time-off.dto";
import { WorksheetDto } from "@/core/features/worksheets/worksheet.dto";
import { MyCalendarEventType } from "@/modules/calendar/types/MyCalendarEventType";
import { toLocaleTimezone } from "@/utils/dates/dateUtils";
import { OnCallDto } from "@/core/features/on-calls/on-call.dto";
import { IRequest } from "@/interfaces/IRequest";
import { CondOperator } from "@nestjsx/crud-request";
import { uniqBy } from "lodash";
import { GroupTimeOffDto } from "@/core/features/times-off/group-time-off.dto";
import { CollectiveAgreementHolidayDto } from "@/core/features/collective-agreement-holidays/collective-agreement-holiday.dto";
import { IsCalendarLoadCurrent } from "@/modules/calendar/types/is-calendar-load-current";

const acceptCalendarLoad: IsCalendarLoadCurrent = (): boolean => true;

export class CalendarsPresenter {
  async getData(
    component: ComponentPublicInstance,
    store: string,
    userId: string,
    dates: Date[],
    isMine?: boolean,
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): Promise<void> {
    if (!this.clearCalendarIfCurrent(component, store, isStillCurrent)) return;
    const from: string = this.getFirstDate(dates);
    const to: string = this.getLastDate(dates);
    await Promise.all([
      this.getTimesOff(component, store, userId, from, to, isMine, isStillCurrent),
      this.getOnCalls(component, store, userId, from, to, isMine, isStillCurrent),
      this.getSchedule(component, store, userId, from, to, isStillCurrent),
      this.getWorksheets(component, store, userId, from, isMine, isStillCurrent),
      this.getWorkHolidays(component, store, userId, dates, isMine, isStillCurrent),
    ]);
  }

  async getGroupDataByOperativeUnit(
    component: ComponentPublicInstance,
    store: string,
    operativeUnitId: string,
    dates: Date[],
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): Promise<void> {
    if (!this.clearCalendarIfCurrent(component, store, isStillCurrent)) return;
    const from: string = this.getFirstDate(dates);
    const to: string = this.getLastDate(dates);
    await this.getTimesOffByOperativeUnit(component, store, operativeUnitId, from, to, isStillCurrent);
  }

  private async getSchedule(
    component: ComponentPublicInstance,
    store: string,
    userId: string,
    from: string,
    to: string,
    isStillCurrent: IsCalendarLoadCurrent
  ): Promise<void> {
    const scheduledMe = (
      await component.$projectScheduleUsersResource.getMySchedules(userId, {
        join: ["project", "project.workplace"],
        filters: { from: from, to: to },
        rowsPerPage: 500,
      })
    ).data;
    if (!isStillCurrent()) return;

    scheduledMe.forEach((schedule: ProjectSchedulesUserDto) =>
      this.registerElement(
        component,
        store,
        schedule,
        schedule.projectId,
        { date: schedule.date },
        "project",
        isStillCurrent
      )
    );
  }

  async getTimesOff(
    component: ComponentPublicInstance,
    store: string,
    userId: string,
    from: string,
    to: string,
    isMine?: boolean,
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): Promise<void> {
    const request = {
      join: ["timeOffType"],
      filters: {
        from: from,
        to: to,
        startDateField: "start",
        endDateField: "end",
        "timeOffHasUsers.userId": userId,
      },
      rowsPerPage: 500,
    };
    const timesOff = (
      isMine
        ? await component.$timesOffResource.getMyTimesOff(userId, request)
        : await component.$timesOffResource.findAll(request)
    ).data;
    if (!isStillCurrent()) return;

    timesOff.forEach((timeOff: TimeOffDto) =>
      this.registerElement(
        component,
        store,
        timeOff,
        timeOff.id,
        {
          start: timeOff.start,
          end: timeOff.end,
        },
        "time-off",
        isStillCurrent
      )
    );
  }

  async getOnCalls(
    component: ComponentPublicInstance,
    store: string,
    userId: string,
    from: string,
    to: string,
    isMine?: boolean,
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): Promise<void> {
    const request: IRequest = {
      join: ["onCallType"],
      filters: {
        from: from,
        to: to,
        startDateField: "date",
        endDateField: "date",
      },
      rowsPerPage: 500,
    };

    if (!isMine) {
      request.customAndFilters = [{ field: "onCallHasUsers.userId", operator: CondOperator.IN, value: [userId] }];
    }

    const onCalls = (
      isMine
        ? await component.$onCallsResource.getMyOnCalls(request, true)
        : await component.$onCallsResource.findAll(request, true)
    ).data;
    if (!isStillCurrent()) return;

    onCalls.forEach((onCall: OnCallDto) =>
      this.registerElement(component, store, onCall, onCall.id, { date: onCall.date }, "on-call", isStillCurrent)
    );
  }

  async getWorksheets(
    component: ComponentPublicInstance,
    store: string,
    userId: string,
    from: string,
    isMine?: boolean,
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): Promise<void> {
    const worksheets: WorksheetDto[] = isMine
      ? await component.$worksheetsResource.getMyMonthlyWorksheetsByStartDate(from)
      : await component.$worksheetsResource.getMonthlyWorksheetsByUser(userId, from);
    if (!isStillCurrent()) return;

    worksheets.forEach((worksheet: WorksheetDto) =>
      this.registerElement(
        component,
        store,
        worksheet,
        worksheet.id,
        { date: worksheet.date },
        "worksheet",
        isStillCurrent
      )
    );
  }

  private async getWorkHolidays(
    component: ComponentPublicInstance,
    store: string,
    userId: string,
    dates: Date[],
    isMine: boolean | undefined,
    isStillCurrent: IsCalendarLoadCurrent
  ): Promise<void> {
    try {
      const years: number[] = Array.from(new Set(dates.map((date: Date) => date.getFullYear()))).sort(
        (left: number, right: number) => left - right
      );
      const requests = years.map((year: number) => {
        return isMine
          ? component.$workHolidaysResource.listMineByYear(year)
          : component.$workHolidaysResource.listUserByYear(userId, year);
      });

      const results: CollectiveAgreementHolidayDto[][] = await Promise.all(requests);
      const holidays: CollectiveAgreementHolidayDto[] = results.flatMap((result) => result);
      this.publishWorkHolidays(component, store, holidays, isStillCurrent);
    } catch (error) {
      // This must not block the calendar if the endpoint is not allowed for the current role.
      this.publishWorkHolidays(component, store, [], isStillCurrent);
      console.warn("Failed to load work calendar holidays for calendar view", error);
    }
  }

  async getTimesOffByOperativeUnit(
    component: ComponentPublicInstance,
    store: string,
    operativeUnitId: string,
    from: string,
    to: string,
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): Promise<void> {
    const request = {
      join: ["timeOffType"],
      filters: {
        from: from,
        to: to,
        startDateField: "start",
        endDateField: "end",
        "timeOffHasUsers.user.operativeUnitId": operativeUnitId,
      },
      rowsPerPage: 500,
    };
    const timesOff = (await component.$timesOffResource.findAll(request)).data;
    if (!isStillCurrent()) return;

    const groupedTimesOff: GroupTimeOffDto[] = [];

    timesOff.forEach((timeOff) => {
      const existingGroup = groupedTimesOff.find(
        (group) =>
          group.timeOffTypeId === timeOff.timeOffTypeId &&
          group.status === timeOff.status &&
          new Date(group.start).getTime() === new Date(timeOff.start).getTime() &&
          new Date(group.end).getTime() === new Date(timeOff.end).getTime()
      );

      if (existingGroup) {
        existingGroup.groupIds = existingGroup.groupIds?.length
          ? [...existingGroup.groupIds, timeOff.id]
          : [existingGroup.id, timeOff.id];
        existingGroup.timeOffHasUsers = uniqBy(
          [...existingGroup.timeOffHasUsers, ...timeOff.timeOffHasUsers],
          "userId"
        );
      } else {
        groupedTimesOff.push({ ...timeOff, timeOffHasUsers: [...timeOff.timeOffHasUsers] } as TimeOffDto);
      }
    });

    groupedTimesOff.forEach((timeOff) =>
      this.registerElement(
        component,
        store,
        timeOff,
        timeOff.id,
        {
          start: timeOff.start,
          end: timeOff.end,
        },
        "time-off",
        isStillCurrent
      )
    );
  }

  registerElement(
    component: ComponentPublicInstance,
    store: string,
    object: TimeOffDto | ProjectSchedulesUserDto | WorksheetDto | OnCallDto,
    id: string,
    dates: { date?: Date; start?: Date; end?: Date },
    type: MyCalendarEventType,
    isStillCurrent: IsCalendarLoadCurrent = acceptCalendarLoad
  ): void {
    if (!isStillCurrent()) return;
    const intervalStart: Date | null = dates.start ? toLocaleTimezone(dates.start) : null;
    const intervalEnd: Date | null = dates.end ? toLocaleTimezone(dates.end) : null;
    if (intervalStart && intervalEnd) {
      const intervalDates: Date[] = eachDayOfInterval({ start: intervalStart, end: intervalEnd });
      for (const date of intervalDates) {
        if (!isStillCurrent()) return;
        this.createCalendarEvent(component, store, object, id, date, type, isStillCurrent);
      }
      return;
    }

    this.createCalendarEvent(component, store, object, id, dates.date, type, isStillCurrent);
  }

  private clearCalendarIfCurrent(
    component: ComponentPublicInstance,
    store: string,
    isStillCurrent: IsCalendarLoadCurrent
  ): boolean {
    if (!isStillCurrent()) return false;
    component.$store.dispatch(`${store}/clear`);
    return isStillCurrent();
  }

  private publishWorkHolidays(
    component: ComponentPublicInstance,
    store: string,
    holidays: CollectiveAgreementHolidayDto[],
    isStillCurrent: IsCalendarLoadCurrent
  ): void {
    if (!isStillCurrent()) return;
    component.$store.dispatch(`${store}/setWorkHolidays`, holidays);
  }

  private createCalendarEvent(
    component: ComponentPublicInstance,
    store: string,
    object: TimeOffDto | ProjectSchedulesUserDto | WorksheetDto | OnCallDto,
    id: string,
    date: Date | undefined,
    type: MyCalendarEventType,
    isStillCurrent: IsCalendarLoadCurrent
  ): void {
    if (!isStillCurrent() || !date) return;
    component.$store.dispatch(`${store}/createEvent`, this.getRegisterObject(object, id, date, type));
  }

  private getFirstDate(dates: Date[]) {
    return formatApiDate(addDays(dates[0], -1));
  }

  private getLastDate(dates: Date[]) {
    return formatApiDate(addDays(dates[dates.length - 1], 1));
  }

  private getRegisterObject(
    object: TimeOffDto | ProjectSchedulesUserDto | WorksheetDto | OnCallDto,
    id: string,
    date: Date,
    type: MyCalendarEventType
  ) {
    return {
      object,
      id,
      date: toLocaleTimezone(date),
      type,
      position: 0,
    };
  }
}
